import { environment } from "@app/relay/relay-environment";
import { getPerspectiveId } from "@app/lib/perspective";
import { useMemo, useState } from "react";
import { commitMutation, graphql } from "react-relay";

import type { categoryCell_AssignStakeholderCategoryMutation } from "@app/queries/categoryCell_AssignStakeholderCategoryMutation.graphql";
import type { categoryCell_AssignStakeholderCategoryPathMutation } from "@app/queries/categoryCell_AssignStakeholderCategoryPathMutation.graphql";

const assignCategoryMutation = graphql`
  mutation categoryCell_AssignStakeholderCategoryMutation(
    $input: AssignStakeholderCategoryInputObject!
  ) {
    assignStakeholderCategory(input: $input) {
      stakeholder {
        id
      }
      errors {
        message
      }
    }
  }
`;

const assignCategoryPathMutation = graphql`
  mutation categoryCell_AssignStakeholderCategoryPathMutation(
    $input: AssignStakeholderCategoryPathInputObject!
  ) {
    assignStakeholderCategoryPath(input: $input) {
      stakeholder {
        id
      }
      errors {
        message
      }
    }
  }
`;

export type CategoryOption = {
  readonly id: string;
  readonly name: string;
  readonly parent?: { readonly id: string } | null;
};

// "Marketing › Salaries › Management" by walking parent ids.
function pathFor(id: string, byId: Map<string, CategoryOption>): string {
  const parts: string[] = [];
  let node: CategoryOption | undefined = byId.get(id);
  let guard = 0;
  while (node && guard < 10) {
    parts.unshift(node.name);
    node = node.parent ? byId.get(node.parent.id) : undefined;
    guard++;
  }
  return parts.join(" › ");
}

// Set a counterparty's category inline: assigns one category at 100%,
// superseding any split, propagating app-wide via Categorizations::Apply.
// The parent owns which row's editor is open (one dropdown at a time).
export default function CategoryCell({
  stakeholderId,
  categories,
  currentCategoryId,
  open,
  onOpenChange,
  onAssigned,
}: {
  stakeholderId: string | null;
  categories: ReadonlyArray<CategoryOption>;
  currentCategoryId?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAssigned: () => void;
}) {
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const byId = useMemo(
    () => new Map(categories.map((c) => [c.id, c])),
    [categories]
  );
  const paths = useMemo(
    () =>
      categories
        .map((c) => ({ id: c.id, path: pathFor(c.id, byId) }))
        .sort((a, b) => a.path.localeCompare(b.path)),
    [categories, byId]
  );

  // Category lives on the stakeholder — nothing to assign without a counterparty.
  if (!stakeholderId) {
    return <span className="text-xs text-gray-300">—</span>;
  }

  const currentPath = currentCategoryId
    ? pathFor(currentCategoryId, byId)
    : null;

  const assign = (categoryId: string) => {
    setSaving(true);
    setError(null);
    commitMutation<categoryCell_AssignStakeholderCategoryMutation>(
      environment,
      {
        mutation: assignCategoryMutation,
        variables: { input: { stakeholderId, categoryId } },
        onCompleted: (response) => {
          setSaving(false);
          const payload = response.assignStakeholderCategory;
          if (payload?.stakeholder) {
            onOpenChange(false);
            onAssigned();
          } else {
            setError(payload?.errors?.[0]?.message ?? "Could not save");
          }
        },
        onError: (err) => {
          setSaving(false);
          setError(err.message);
        },
      }
    );
  };

  const createPath = (path: string) => {
    setSaving(true);
    setError(null);
    commitMutation<categoryCell_AssignStakeholderCategoryPathMutation>(
      environment,
      {
        mutation: assignCategoryPathMutation,
        variables: {
          input: {
            stakeholderId,
            path,
            perspectiveId: getPerspectiveId() ?? undefined,
          },
        },
        onCompleted: (response) => {
          setSaving(false);
          const payload = response.assignStakeholderCategoryPath;
          if (payload?.stakeholder) {
            onOpenChange(false);
            onAssigned();
          } else {
            setError(payload?.errors?.[0]?.message ?? "Could not save");
          }
        },
        onError: (err) => {
          setSaving(false);
          setError(err.message);
        },
      }
    );
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => {
          setQuery("");
          setError(null);
          onOpenChange(true);
        }}
        className={`text-left text-xs hover:underline decoration-dotted ${
          currentPath ? "text-gray-700" : "text-amber-600"
        }`}
      >
        {currentPath ?? "Uncategorized — assign"}
      </button>
    );
  }

  const trimmed = query.trim().toLowerCase();
  const matches = (
    trimmed
      ? paths.filter((p) => p.path.toLowerCase().includes(trimmed))
      : paths
  ).slice(0, 10);
  const typedSegments = query
    .split(/[>›]/)
    .map((s) => s.trim())
    .filter(Boolean);
  const normalizedPath = typedSegments.join(" › ");
  const canCreate =
    typedSegments.length > 0 &&
    !paths.some((p) => p.path.toLowerCase() === normalizedPath.toLowerCase());

  return (
    <div className="relative">
      <span className="relative inline-block">
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Escape" && onOpenChange(false)}
          placeholder="Type a category…"
          disabled={saving}
          className="rounded border pl-2 pr-6 py-1 text-xs w-56"
        />
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          aria-label="Close"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-600 text-xs"
        >
          ✕
        </button>
      </span>
      <div className="absolute z-10 mt-1 w-72 bg-white rounded border shadow-lg text-xs max-h-72 overflow-y-auto">
        {error && <p className="px-2 py-1.5 text-red-600">{error}</p>}
        {matches.map((m) => (
          <button
            key={m.id}
            type="button"
            disabled={saving || m.id === currentCategoryId}
            onClick={() => assign(m.id)}
            className={`block w-full text-left px-2 py-1.5 hover:bg-gray-50 ${
              m.id === currentCategoryId ? "text-gray-400" : ""
            }`}
          >
            {m.path}
          </button>
        ))}
        {matches.length === 0 && !canCreate && (
          <p className="px-2 py-1.5 text-gray-400">No matching category</p>
        )}
        {canCreate && (
          <button
            type="button"
            disabled={saving}
            onClick={() => createPath(query)}
            className="block w-full text-left px-2 py-1.5 font-medium text-emerald-700 hover:bg-emerald-50 border-t"
          >
            {saving ? "Saving…" : `Create new subcategory: "${normalizedPath}"`}
          </button>
        )}
      </div>
    </div>
  );
}
